import { VuexResponse } from '@/helpers/vuex-action-builder'
import { MaxPreferredQueueSize } from '@/shared/constants/booking/BookingSelectEducators'
import { moveUp, moveDown } from '@/helpers/reorderable-array-helpers'
import CandidateSortOrder from '@/shared/constants/candidate/CandidateSortOrder'
import { BookableEducatorQueryRequestDTO } from '@/models/candidate/BookableEducatorQueryRequestDTO'
import { normaliseQueryContext } from '@/helpers/booking-create-helpers'

const { NameAsc } = CandidateSortOrder

const getDefaultState = () => ({
  invalidEducatorIds: [],
  preferredQueue: {
    candidates: [],
    isDragging: false,
  },
  bookableEducators: {
    isLoading: false,
    hasError: false,
    candidates: [],
    currentPage: 1,
    totalPages: 1,
    sortOrder: NameAsc,
    searchTerm: '',
    requestId: 0, // used to handle repeat create booking form changes triggering multiple fetches
    needRefresh: true,
    queryContext: null,
    querySignature: null,
  },
})

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    preferredQueue: (state) => state.preferredQueue.candidates,
    invalidEducatorIds: (state) => state.invalidEducatorIds,
    isPreferredQueueDragging: (state) => state.preferredQueue.isDragging,
    isQueueFull: (state) =>
      state.preferredQueue.candidates.length >= MaxPreferredQueueSize,
    preferredEducatorIds: (state) =>
      state.preferredQueue.candidates.map(({ id }) => id),
    priorityCandidates: (state) =>
      state.preferredQueue.candidates.map(({ id }, index) => ({
        id,
        priority: index + 1,
      })),
    bookableEducatorQueryContext: (state) =>
      state.bookableEducators.queryContext,
    bookableEducators: (state) => state.bookableEducators,
    isBookableEducatorsLoading: (state) => state.bookableEducators.isLoading,
  },
  mutations: {
    SET_INVALID_EDUCATOR_IDS(state, educatorIds) {
      state.invalidEducatorIds = educatorIds
    },
    SET_BOOKABLE_EDUCATOR_QUERY_CONTEXT(state, { queryContext, signature }) {
      state.bookableEducators = {
        ...getDefaultState().bookableEducators,
        queryContext,
        querySignature: signature,
      }
    },
    SET_PREFERRED_QUEUE_STATE(state, payload) {
      Object.assign(state.preferredQueue, payload)
    },
    SET_BOOKABLE_EDUCATORS_STATE(state, payload) {
      Object.assign(state.bookableEducators, payload)
    },
    RESET_STATE(state) {
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    syncBookableEducatorQueryContext({ commit, state }, queryContext) {
      const normalisedQueryContext = normaliseQueryContext(queryContext)
      const signature = JSON.stringify(normalisedQueryContext)

      if (signature === state.bookableEducators.querySignature) return

      const hasPreviousQueryContext =
        state.bookableEducators.querySignature !== null

      commit('SET_BOOKABLE_EDUCATOR_QUERY_CONTEXT', {
        queryContext: normalisedQueryContext,
        signature,
      })

      if (!hasPreviousQueryContext) return

      commit('SET_INVALID_EDUCATOR_IDS', [])
      commit('SET_PREFERRED_QUEUE_STATE', { candidates: [] })
    },
    async fetchBookableEducators({ commit, state }) {
      if (!state.bookableEducators.queryContext) return

      const requestId = state.bookableEducators.requestId + 1
      const querySignature = state.bookableEducators.querySignature
      const payload = new BookableEducatorQueryRequestDTO({
        ...state.bookableEducators.queryContext,
        sortOrder: state.bookableEducators.sortOrder,
        searchTerm: state.bookableEducators.searchTerm,
        pageNumber: state.bookableEducators.currentPage,
      })

      commit('SET_BOOKABLE_EDUCATORS_STATE', {
        requestId,
        isLoading: true,
        hasError: false,
      })

      const { data, error } = await new VuexResponse(commit)
        .request(() => this.$api.candidate.getBookableEducators(payload))
        .go()

      if (requestId !== state.bookableEducators.requestId) return
      if (querySignature !== state.bookableEducators.querySignature) return

      // TODO: handle error codes and messages from API response
      if (error) {
        commit('SET_BOOKABLE_EDUCATORS_STATE', {
          candidates: [],
          isLoading: false,
          hasError: true,
          needRefresh: false,
        })
        return
      }

      commit('SET_BOOKABLE_EDUCATORS_STATE', {
        candidates: data?.candidates ?? [],
        currentPage: data?.currentPage ?? 1,
        totalPages: data?.totalPages ?? 1,
        isLoading: false,
        hasError: false,
        needRefresh: false,
      })
    },
    async searchBookableEducators({ commit, dispatch }) {
      commit('SET_BOOKABLE_EDUCATORS_STATE', { currentPage: 1 })
      await dispatch('fetchBookableEducators')
    },

    async changeBookableEducatorPage({ commit, dispatch }, page) {
      commit('SET_BOOKABLE_EDUCATORS_STATE', { currentPage: page })
      await dispatch('fetchBookableEducators')
    },

    setBookableEducatorSearchTerm({ commit }, searchTerm) {
      commit('SET_BOOKABLE_EDUCATORS_STATE', { searchTerm: searchTerm ?? '' })
    },

    setPreferredQueue({ commit }, queue) {
      const uniqueCandidates = queue.filter(
        (educator, index) =>
          queue.findIndex(({ id }) => id === educator.id) === index
      )

      commit('SET_PREFERRED_QUEUE_STATE', {
        candidates: uniqueCandidates.slice(0, MaxPreferredQueueSize),
      })
    },

    setPreferredQueueDragging({ commit }, isDragging) {
      commit('SET_PREFERRED_QUEUE_STATE', { isDragging })
    },

    addEducator({ commit, state }, educator) {
      const { candidates } = state.preferredQueue

      if (candidates.length >= MaxPreferredQueueSize) return
      if (candidates.some(({ id }) => id === educator.id)) return
      if (state.invalidEducatorIds.includes(educator.id)) return

      commit('SET_PREFERRED_QUEUE_STATE', {
        candidates: [...candidates, educator],
      })
    },

    setInvalidEducators({ commit, state }, educatorIds) {
      const newInvalidEducatorIds = (Array.isArray(educatorIds)
        ? educatorIds
        : []
      ).filter((educatorId) => typeof educatorId === 'number')

      if (!newInvalidEducatorIds.length) return

      const invalidEducatorIds = [
        ...new Set([...state.invalidEducatorIds, ...newInvalidEducatorIds]),
      ]
      const candidates = state.preferredQueue.candidates.filter(
        ({ id }) => !invalidEducatorIds.includes(id)
      )

      commit('SET_INVALID_EDUCATOR_IDS', invalidEducatorIds)
      commit('SET_PREFERRED_QUEUE_STATE', { candidates })
    },

    removeFromQueue({ commit, state }, educatorId) {
      commit('SET_PREFERRED_QUEUE_STATE', {
        candidates: state.preferredQueue.candidates.filter(
          ({ id }) => id !== educatorId
        ),
      })
    },

    moveEducatorUp({ commit, state }, index) {
      commit('SET_PREFERRED_QUEUE_STATE', {
        candidates: moveUp(state.preferredQueue.candidates, index),
      })
    },

    moveEducatorDown({ commit, state }, index) {
      commit('SET_PREFERRED_QUEUE_STATE', {
        candidates: moveDown(state.preferredQueue.candidates, index),
      })
    },
    resetSelectEducators({ commit }) {
      commit('RESET_STATE')
    },
  },
}
